import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment,
  Box,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  ExpandMore,
  RecordVoiceOver,
  Settings,
  PhoneAndroid,
  Refresh,
  Loop,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1,
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column",
  },
}));

// Modelos disponibles de ElevenLabs
const elevenLabsModels = [
  { value: "eleven_multilingual_v2", label: "Multilingual V2 (Recomendado)" },
  { value: "eleven_turbo_v2_5", label: "Turbo V2.5 (Más rápido)" },
  { value: "eleven_turbo_v2", label: "Turbo V2" },
  { value: "eleven_monolingual_v1", label: "Monolingual V1" },
];

// Schema de validación
const VoiceToVoiceSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Muy corto!")
    .max(100, "Muy largo!")
    .required("Obligatorio"),
  whatsappConnection: Yup.string().required("Seleccione una conexión de WhatsApp"),
  apiKey: Yup.string().required("Informe la API Key de ElevenLabs"),
  voiceId: Yup.string().required("Seleccione una voz"),
  model: Yup.string().required("Seleccione un modelo"),
  systemPrompt: Yup.string()
    .min(20, "Muy corto!")
    .required("Describa cómo debe comportarse el asistente de voz"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(1, "Máximo 1")
    .required("Informe la temperatura"),
  maxTokens: Yup.number()
    .min(100, "Mínimo 100 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe el número máximo de tokens"),
});

const FlowBuilderVoiceToVoiceModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    whatsappConnection: "",
    apiKey: "",
    voiceId: "",
    model: "eleven_multilingual_v2",
    systemPrompt: "",
    temperature: 0.7,
    maxTokens: 1000,
    stability: 0.5,
    similarityBoost: 0.75,
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [labels, setLabels] = useState({
    title: "Agregar Voice to Voice al flujo",
    btn: "Agregar",
  });
  
  // Loop configuration state (Task 12.1)
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [maxIterations, setMaxIterations] = useState(10);
  const [timeoutSeconds, setTimeoutSeconds] = useState(300);
  const [exitKeywords, setExitKeywords] = useState("");
  const [loopErrors, setLoopErrors] = useState({
    maxIterations: "",
    timeoutSeconds: "",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Voice to Voice del flujo",
        btn: "Guardar",
      });
      const voiceToVoiceData = data?.data?.voiceToVoice || {};
      setIntegration({
        ...initialState,
        ...voiceToVoiceData,
      });
      
      // Load loop configuration (Task 12.1)
      if (voiceToVoiceData.loop) {
        setLoopEnabled(voiceToVoiceData.loop.enabled || false);
        setMaxIterations(voiceToVoiceData.loop.maxIterations || 10);
        setTimeoutSeconds(voiceToVoiceData.loop.timeoutSeconds || 300);
        setExitKeywords(
          Array.isArray(voiceToVoiceData.loop.exitKeywords)
            ? voiceToVoiceData.loop.exitKeywords.join(", ")
            : ""
        );
      } else {
        // Reset to defaults if no loop config
        setLoopEnabled(false);
        setMaxIterations(10);
        setTimeoutSeconds(300);
        setExitKeywords("");
      }
      
      // Cargar voces si hay API key
      if (voiceToVoiceData.apiKey) {
        console.log("[VoiceToVoice] Cargando voces al abrir modal en modo edición");
        loadVoices(voiceToVoiceData.apiKey);
      }
    } else if (open === "create") {
      setLabels({
        title: "Agregar Voice to Voice al flujo",
        btn: "Agregar",
      });
      setIntegration(initialState);
      setAvailableVoices([]); // Limpiar voces al crear nuevo
      
      // Reset loop configuration to defaults (Task 12.1)
      setLoopEnabled(false);
      setMaxIterations(10);
      setTimeoutSeconds(300);
      setExitKeywords("");
    }

    // Cargar conexiones de WhatsApp
    loadWhatsAppConnections();
  }, [open, data]);

  const loadWhatsAppConnections = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsappConnections(data);
    } catch (err) {
      toastError(err);
    }
  };

  const loadVoices = async (apiKey) => {
    // No intentar cargar si no hay API key o es muy corta
    if (!apiKey || apiKey.length < 20) {
      console.log("[VoiceToVoice] API key muy corta:", apiKey?.length || 0);
      setAvailableVoices([]);
      return;
    }
    
    console.log("[VoiceToVoice] Cargando voces con API key de longitud:", apiKey.length);
    setLoadingVoices(true);
    try {
      const { data } = await api.get("/elevenlabs/voices", {
        headers: {
          "xi-api-key": apiKey,
        },
      });
      console.log("[VoiceToVoice] Voces recibidas:", data);
      const voices = data.voices || [];
      console.log("[VoiceToVoice] Total de voces:", voices.length);
      setAvailableVoices(voices);
      
      if (voices.length === 0) {
        toast.warning("No se encontraron voces disponibles en tu cuenta de ElevenLabs");
      }
    } catch (err) {
      console.error("[VoiceToVoice] Error loading voices:", err);
      console.error("[VoiceToVoice] Error response:", err.response);
      // No mostrar error si es por API key inválida (el usuario aún está escribiendo)
      if (err.response?.status !== 401) {
        toastError(err);
      } else {
        toast.error("API Key inválida. Verifica tu clave de ElevenLabs");
      }
      setAvailableVoices([]);
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleClose = () => {
    close(null);
  };

  const handleSave = (values, { setSubmitting }) => {
    // Validate loop configuration (Task 12.4)
    if (loopEnabled) {
      let hasErrors = false;
      const errors = { maxIterations: "", timeoutSeconds: "" };
      
      if (!Number.isInteger(maxIterations) || maxIterations <= 0) {
        errors.maxIterations = "Debe ser un número entero positivo";
        hasErrors = true;
      }
      
      if (typeof timeoutSeconds !== "number" || timeoutSeconds <= 0) {
        errors.timeoutSeconds = "Debe ser un número positivo";
        hasErrors = true;
      }
      
      if (hasErrors) {
        setLoopErrors(errors);
        setSubmitting(false);
        toast.error("Por favor corrija los errores en la configuración del loop");
        return;
      }
    }
    
    // Clear any previous errors
    setLoopErrors({ maxIterations: "", timeoutSeconds: "" });
    
    // Buscar el nombre de la conexión de WhatsApp seleccionada
    const selectedConnection = whatsappConnections.find(
      conn => conn.id === parseInt(values.whatsappConnection)
    );
    
    const voiceToVoiceData = {
      ...values,
      type: "voiceToVoice",
      // Guardar tanto el ID como el nombre/número de la conexión
      whatsappConnectionId: values.whatsappConnection,
      whatsappConnectionName: selectedConnection 
        ? `${selectedConnection.name} - ${selectedConnection.number}`
        : "No asignada",
    };
    
    // Add loop configuration if enabled (Task 12.5)
    if (loopEnabled) {
      // Parse exit keywords string into array (Task 12.5)
      const exitKeywordsArray = exitKeywords
        .split(",")
        .map(keyword => keyword.trim())
        .filter(keyword => keyword.length > 0);
      
      voiceToVoiceData.loop = {
        enabled: true,
        maxIterations: maxIterations,
        timeoutSeconds: timeoutSeconds,
        exitKeywords: exitKeywordsArray,
        exitKeywordsCaseSensitive: false,
      };
    } else {
      // Explicitly set loop to disabled
      voiceToVoiceData.loop = {
        enabled: false,
        maxIterations: 10,
        timeoutSeconds: 300,
        exitKeywords: [],
        exitKeywordsCaseSensitive: false,
      };
    }

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { voiceToVoice: voiceToVoiceData },
      });
    } else if (open === "create") {
      onSave({
        voiceToVoice: voiceToVoiceData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const handleApiKeyChange = (e, setFieldValue) => {
    const newApiKey = e.target.value;
    setFieldValue("apiKey", newApiKey);
    
    console.log("[VoiceToVoice] API Key cambiada, longitud:", newApiKey.length);
    
    // Cargar voces cuando se ingresa una API key válida
    if (newApiKey && newApiKey.length >= 20) {
      console.log("[VoiceToVoice] Cargando voces con API key:", newApiKey.substring(0, 10) + "...");
      loadVoices(newApiKey);
    } else {
      console.log("[VoiceToVoice] API key muy corta, esperando más caracteres...");
      setAvailableVoices([]);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={VoiceToVoiceSchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURACIÓN BÁSICA */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configuración Básica
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <Field
                      as={TextField}
                      label="Nombre del Asistente de Voz"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.whatsappConnection && Boolean(errors.whatsappConnection)}
                    >
                      <InputLabel>Conexión de WhatsApp</InputLabel>
                      <Field
                        as={Select}
                        label="Conexión de WhatsApp"
                        name="whatsappConnection"
                      >
                        <MenuItem value="">
                          <em>Seleccione una conexión</em>
                        </MenuItem>
                        {whatsappConnections.map((conn) => (
                          <MenuItem key={conn.id} value={conn.id}>
                            {conn.name} - {conn.number}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.whatsappConnection && errors.whatsappConnection && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.whatsappConnection}
                        </div>
                      )}
                    </FormControl>

                    <Field
                      as={TextField}
                      label="Prompt del Sistema"
                      name="systemPrompt"
                      error={touched.systemPrompt && Boolean(errors.systemPrompt)}
                      helperText={
                        touched.systemPrompt && errors.systemPrompt ||
                        "Describe cómo debe comportarse el asistente de voz, qué información debe recopilar, etc."
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={6}
                      multiline
                      placeholder="Eres un asistente de voz amigable que ayuda a los clientes con..."
                    />

                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURACIÓN DE ELEVENLABS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <RecordVoiceOver />
                      Configuración de ElevenLabs
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={TextField}
                        label="API Key de ElevenLabs"
                        name="apiKey"
                        type={showApiKey ? "text" : "password"}
                        error={touched.apiKey && Boolean(errors.apiKey)}
                        helperText={touched.apiKey && errors.apiKey}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                        onChange={(e) => handleApiKeyChange(e, setFieldValue)}
                        onBlur={(e) => {
                          // Intentar cargar voces también al perder el foco
                          const apiKey = e.target.value;
                          if (apiKey && apiKey.length >= 20) {
                            loadVoices(apiKey);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>

                    <Box display="flex" gap={1} alignItems="flex-start">
                      <FormControl
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        error={touched.voiceId && Boolean(errors.voiceId)}
                        disabled={loadingVoices || availableVoices.length === 0}
                      >
                        <InputLabel>Voz</InputLabel>
                        <Field as={Select} label="Voz" name="voiceId">
                          <MenuItem value="">
                            <em>
                              {loadingVoices
                                ? "Cargando voces..."
                                : availableVoices.length === 0
                                ? "Ingrese API Key para cargar voces"
                                : "Seleccione una voz"}
                            </em>
                          </MenuItem>
                          {availableVoices.map((voice) => (
                            <MenuItem key={voice.voice_id} value={voice.voice_id}>
                              {voice.name}
                            </MenuItem>
                          ))}
                        </Field>
                        {touched.voiceId && errors.voiceId && (
                          <div style={{ color: "red", fontSize: "12px" }}>
                            {errors.voiceId}
                          </div>
                        )}
                      </FormControl>
                      
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const apiKey = values.apiKey;
                          console.log("[VoiceToVoice] Botón Cargar Voces clickeado");
                          console.log("[VoiceToVoice] API Key length:", apiKey?.length || 0);
                          console.log("[VoiceToVoice] Voces actuales:", availableVoices.length);
                          
                          if (apiKey && apiKey.length >= 20) {
                            console.log("[VoiceToVoice] Recargando voces manualmente...");
                            loadVoices(apiKey);
                          } else {
                            toast.error("Ingrese un API Key válido primero (mínimo 20 caracteres)");
                          }
                        }}
                        disabled={loadingVoices || !values.apiKey || values.apiKey.length < 20}
                        style={{ marginTop: 8, minWidth: 120 }}
                      >
                        {loadingVoices ? "Cargando..." : "Cargar Voces"}
                      </Button>
                    </Box>

                    {/* Información de depuración */}
                    {values.apiKey && values.apiKey.length >= 20 && (
                      <Box mt={1}>
                        <Typography variant="caption" color="textSecondary">
                          API Key: {values.apiKey.substring(0, 10)}... ({values.apiKey.length} caracteres)
                          <br />
                          Voces cargadas: {availableVoices.length}
                          {availableVoices.length > 0 && (
                            <span> - {availableVoices.map(v => v.name).join(", ")}</span>
                          )}
                        </Typography>
                      </Box>
                    )}

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.model && Boolean(errors.model)}
                    >
                      <InputLabel>Modelo</InputLabel>
                      <Field as={Select} label="Modelo" name="model">
                        {elevenLabsModels.map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.model && errors.model && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.model}
                        </div>
                      )}
                    </FormControl>

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Stability"
                        name="stability"
                        error={touched.stability && Boolean(errors.stability)}
                        helperText="0 = más variado, 1 = más estable"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{
                          step: "0.05",
                          min: "0",
                          max: "1",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Similarity Boost"
                        name="similarityBoost"
                        error={touched.similarityBoost && Boolean(errors.similarityBoost)}
                        helperText="0 = más diverso, 1 = más similar"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{
                          step: "0.05",
                          min: "0",
                          max: "1",
                        }}
                      />
                    </div>

                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURACIÓN AVANZADA */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <PhoneAndroid />
                      Configuración Avanzada
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText="0 = conservador, 1 = creativo"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{
                          step: "0.1",
                          min: "0",
                          max: "1",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText="Tamaño máximo de la respuesta"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{
                          min: "100",
                          max: "4000",
                        }}
                      />
                    </div>

                    <Box mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        💡 <strong>Tip:</strong> El asistente de voz procesará automáticamente
                        los audios de WhatsApp y responderá con voz usando ElevenLabs.
                      </Typography>
                    </Box>

                  </AccordionDetails>
                </Accordion>

                {/* LOOP SETTINGS (Tasks 12.2 and 12.3) */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Loop />
                      Configuración de Loop (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Habilita el loop para permitir conversaciones continuas donde el asistente
                        puede procesar múltiples mensajes de voz en secuencia.
                      </Typography>
                    </Box>

                    <FormControl fullWidth margin="dense">
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body1">
                          Habilitar Loop
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                            {loopEnabled ? "Activado" : "Desactivado"}
                          </Typography>
                          <Button
                            variant={loopEnabled ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            onClick={() => setLoopEnabled(!loopEnabled)}
                          >
                            {loopEnabled ? "Desactivar" : "Activar"}
                          </Button>
                        </Box>
                      </Box>
                    </FormControl>

                    {loopEnabled && (
                      <Box mt={2}>
                        <TextField
                          label="Máximo de Iteraciones"
                          type="number"
                          value={maxIterations}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            setMaxIterations(value);
                            // Clear error when user types
                            if (value > 0 && Number.isInteger(value)) {
                              setLoopErrors(prev => ({ ...prev, maxIterations: "" }));
                            }
                          }}
                          error={Boolean(loopErrors.maxIterations)}
                          helperText={
                            loopErrors.maxIterations ||
                            "Número máximo de ciclos de conversación (por defecto: 10)"
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          inputProps={{
                            min: "1",
                            max: "100",
                          }}
                        />

                        <TextField
                          label="Timeout (segundos)"
                          type="number"
                          value={timeoutSeconds}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            setTimeoutSeconds(value);
                            // Clear error when user types
                            if (value > 0) {
                              setLoopErrors(prev => ({ ...prev, timeoutSeconds: "" }));
                            }
                          }}
                          error={Boolean(loopErrors.timeoutSeconds)}
                          helperText={
                            loopErrors.timeoutSeconds ||
                            "Duración máxima del loop en segundos (por defecto: 300 = 5 minutos)"
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          inputProps={{
                            min: "30",
                            max: "3600",
                          }}
                        />

                        <TextField
                          label="Palabras Clave de Salida"
                          value={exitKeywords}
                          onChange={(e) => setExitKeywords(e.target.value)}
                          helperText="Palabras o frases separadas por comas que terminarán el loop (ej: adiós, chao, terminar, salir, gracias)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="adiós, chao, terminar, salir"
                        />

                        <Box mt={2}>
                          <Typography variant="caption" color="textSecondary">
                            💡 <strong>Tip:</strong> El loop se detendrá automáticamente cuando se alcance
                            el máximo de iteraciones, el timeout, o cuando el usuario diga una palabra clave de salida.
                          </Typography>
                        </Box>
                      </Box>
                    )}

                  </AccordionDetails>
                </Accordion>

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  className={classes.btnWrapper}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderVoiceToVoiceModal;
